import React, { memo } from 'react';
import { Loader2, ArrowLeft, Upload } from 'lucide-react';
import { NodeStatus } from '@/types';

interface GenerationPreviewProps {
  isWorking: boolean;
  onGenerate: () => void;
  isUpscaleMode?: boolean;
  onBackToImg2Img?: () => void;
  canGenerate?: boolean;
  /** v1.4.25 取消生成：生成中发送按钮原地切换为停止按钮（图标=实心圆角红色方块，点击取消） */
  onCancel?: () => void;
  /** 积分系统 P1：本次生成预计消耗积分（由父组件按模型定价计算传入；upscale 分支展示） */
  credits?: number;
}

const GenerationPreviewInner: React.FC<GenerationPreviewProps> = ({
  isWorking,
  onGenerate,
  isUpscaleMode = false,
  onBackToImg2Img,
  canGenerate = true,
  onCancel,
  credits = 0,
}) => {
  // v1.4.25 取消生成：生成中发送按钮原地切换为停止按钮（样式随所在面板的发送按钮规格：普通 32×32/8px，upscale 36×36/12px），图标为实心圆角红色方块
  const cancelButton = (sizeClass: string) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onCancel?.();
      }}
      title="取消生成"
      className={`flex items-center justify-center bg-white transition-transform hover:scale-105 cursor-pointer ${sizeClass}`}
    >
      <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="10" height="10" rx="2.5" fill="#ef4444" />
      </svg>
    </button>
  );

  if (isUpscaleMode) {
    // v1.4.24：upscale 分支补齐积分显示，对齐脚本面板 footer 三件套视觉（积分消耗由父组件按 upscale 模型定价传入；upscale 无提示词输入区，不加翻译按钮）
    return (
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-2">
          {onBackToImg2Img && (
            <button
              onClick={onBackToImg2Img}
              className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.06] border border-white/[0.10] text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
              title="返回图生图"
            >
              <ArrowLeft size={16} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* 积分消耗显示：放发送按钮左边（token-icon 同顶部积分胶囊） */}
          <span className="flex items-center gap-1 text-[12px] text-white/40" title="本次生成预计消耗积分">
            <img src="/icons/home/token-icon.svg" alt="" width={12} height={12} draggable={false} />
            {/* 预留三位数积分宽度（tabular-nums 等宽数字），位数变化不引起布局跳动 */}
            <span className="min-w-[22px] tabular-nums text-left">{credits}</span>
          </span>
          {isWorking && onCancel ? (
            // v1.4.25 生成中：发送按钮原地切换为停止按钮
            cancelButton('w-9 h-9 rounded-xl')
          ) : (
            <button
              onClick={onGenerate}
              disabled={isWorking}
              className={`flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-300 ${
                isWorking
                  ? 'bg-white/5 text-white/40 cursor-not-allowed'
                  : 'bg-white text-black hover:bg-white/90 hover:scale-105 active:scale-95'
              }`}
            >
              {isWorking ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            </button>
          )}
        </div>
      </div>
    );
  }

  // v1.4.12：对齐脚本节点底部参数面板发送按钮基准——32×32 / 圆角 8px / 白底 + send-arrow.svg；无输入内容（canGenerate=false）或生成中置灰
  return (
    <div className="flex items-center gap-3">
      {isWorking && onCancel ? (
        // v1.4.25 生成中：发送按钮原地切换为停止按钮
        cancelButton('w-8 h-8 rounded-[8px]')
      ) : (
        <button
          onClick={onGenerate}
          disabled={isWorking || !canGenerate}
          title={isWorking ? '生成中...' : '生成'}
          className="flex items-center justify-center w-8 h-8 bg-white rounded-[8px] text-[#222222] transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
        >
          {isWorking ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <img src="/icons/send-arrow.svg" alt="生成" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
          )}
        </button>
      )}
    </div>
  );
};

export const GenerationPreview = memo(GenerationPreviewInner);
